import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import { useAuth } from '../../context/AuthContext';
import { updateCompanyProfile } from '../../api/companyProfile';
import {
  createMarriageGratisTier, deleteMarriageGratisTier, listMarriageGratisTiers, updateMarriageGratisTier,
} from '../../api/marriageGratis';
import './MarriageGratisPage.css';

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Superviseur' || filter === 'Banque') return { bank: scopeId };
  return {};
};

const emptyNewTier = { min_amount: '', max_amount: '', free_count: '' };

const MarriageGratisPage = () => {
  const { company, setCompany } = useAuth();
  const [enabled, setEnabled] = useState(company?.marriage_gratis_enabled || false);
  const [mode, setMode] = useState(company?.marriage_gratis_mode || 'from_borlette');
  const [savingActivation, setSavingActivation] = useState(false);
  const [activationError, setActivationError] = useState('');
  const [activationSaved, setActivationSaved] = useState(false);

  const handleSaveActivation = async (e) => {
    e.preventDefault();
    setSavingActivation(true);
    setActivationError('');
    setActivationSaved(false);
    try {
      const formData = new FormData();
      formData.append('marriage_gratis_enabled', enabled ? 'true' : 'false');
      formData.append('marriage_gratis_mode', mode);
      const updated = await updateCompanyProfile(formData);
      setCompany(updated);
      setActivationSaved(true);
    } catch (err) {
      setActivationError('Sauvegarde impossible.');
    } finally {
      setSavingActivation(false);
    }
  };

  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const [newTier, setNewTier] = useState(emptyNewTier);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const handleFilterChange = (opt) => {
    setFilter(opt);
    setScopeId('');
    setScopeName('');
    setSearched(false);
  };

  const load = async (e) => {
    if (e) e.preventDefault();
    if (filter !== 'Tout' && !scopeId) {
      setError("Choisis d'abord une cible.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listMarriageGratisTiers(scopeParams(filter, scopeId));
      setTiers(data);
      setSearched(true);
    } catch (err) {
      setError('Impossible de charger les paliers.');
    } finally {
      setLoading(false);
    }
  };

  // Portée "Tout" (par défaut) : on charge la liste des paliers automatiquement,
  // au montage et à chaque retour sur "Tout" — de là on peut modifier/supprimer/
  // ajouter directement. Les portées ciblées (Groupe/Superviseur/Banque) exigent
  // toujours de choisir une cible puis "Chercher".
  useEffect(() => {
    if (filter === 'Tout') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateTierField = (id, field, value) => {
    setTiers((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const handleSaveTier = async (tier) => {
    setSavingId(tier.id);
    setError('');
    try {
      const scope = scopeParams(filter, scopeId);
      const updated = await updateMarriageGratisTier(tier.id, {
        min_amount: tier.min_amount,
        max_amount: tier.max_amount,
        free_count: tier.free_count,
        group: scope.group || null,
        bank: scope.bank || null,
      });
      setTiers((prev) => prev.map((t) => (t.id === tier.id ? updated : t)));
    } catch (err) {
      setError('Sauvegarde impossible pour ce palier — verifie min <= max.');
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteTier = async (id) => {
    setSavingId(id);
    setError('');
    try {
      await deleteMarriageGratisTier(id);
      setTiers((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreateTier = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const scope = scopeParams(filter, scopeId);
      const created = await createMarriageGratisTier({
        min_amount: newTier.min_amount,
        max_amount: newTier.max_amount,
        free_count: newTier.free_count,
        group: scope.group || null,
        bank: scope.bank || null,
      });
      setTiers((prev) => [...prev, created]);
      setNewTier(emptyNewTier);
    } catch (err) {
      setError('Creation impossible — verifie min <= max.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <p className="mg-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <span className="bc-current">Bonos</span>
        {' >> '}
        <span className="bc-current">Marriage Gratis</span>
      </p>

      <div className="mg-card mg-activation-card">
        <div className="mg-card-header">
          <span className="mg-card-title">Activation</span>
        </div>
        <form className="mg-card-body" onSubmit={handleSaveActivation}>
          <div className="mg-row">
            <label className="mg-label">Marriage Gratis</label>
            <label className="mg-checkbox-label">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Activer
            </label>
          </div>

          {enabled && (
            <div className="mg-row mg-mode-row">
              <label className="mg-label">Numéros gratuits</label>
              <div className="mg-radio-col">
                <label className="mg-radio-label">
                  <input
                    type="radio"
                    name="marriageGratisMode"
                    checked={mode === 'from_borlette'}
                    onChange={() => setMode('from_borlette')}
                  />
                  Via boules jouées
                </label>
                <label className="mg-radio-label">
                  <input
                    type="radio"
                    name="marriageGratisMode"
                    checked={mode === 'random'}
                    onChange={() => setMode('random')}
                  />
                  Aléatoire
                </label>
              </div>
            </div>
          )}

          {activationError && <p className="mg-error">{activationError}</p>}
          {activationSaved && <p className="mg-saved">Sauvegarde.</p>}

          <button type="submit" className="mg-save-row-btn mg-activation-save-btn" disabled={savingActivation}>
            {savingActivation ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      </div>

      <div className="mg-card">
        <div className="mg-card-header">
          <span className="mg-card-title">Paliers Marriage Gratis</span>
        </div>
        <div className="mg-card-body">
          <div className="mg-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="mg-radio-label">
                <input
                  type="radio"
                  name="mgFilter"
                  checked={filter === opt}
                  onChange={() => handleFilterChange(opt)}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="mg-row mg-scope-row">
              <label className="mg-label">{filter}</label>
              {(filter === 'Banque' || filter === 'Superviseur') ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="mg-input mg-input-sm"
                  iconClass="mg-icon-btn"
                  nameClass="mg-scope-name"
                  errClass="mg-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="mg-input mg-input-sm"
                  iconClass="mg-icon-btn"
                  nameClass="mg-scope-name"
                  errClass="mg-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={load} className="mg-search-form">
            <button type="submit" className="mg-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>
          </form>

          {error && <p className="mg-error">{error}</p>}

          {searched && (
            <>
              <table className="mg-table">
                <thead>
                  <tr>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Gratis</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier) => (
                    <tr key={tier.id}>
                      <td><input className="mg-cell-input" value={tier.min_amount} onChange={(e) => updateTierField(tier.id, 'min_amount', e.target.value)} /></td>
                      <td><input className="mg-cell-input" value={tier.max_amount} onChange={(e) => updateTierField(tier.id, 'max_amount', e.target.value)} /></td>
                      <td><input className="mg-cell-input" value={tier.free_count} onChange={(e) => updateTierField(tier.id, 'free_count', e.target.value)} /></td>
                      <td className="mg-actions">
                        <button type="button" className="mg-save-row-btn" disabled={savingId === tier.id} onClick={() => handleSaveTier(tier)}>Sauver</button>
                        <button type="button" className="mg-delete-btn" disabled={savingId === tier.id} onClick={() => handleDeleteTier(tier.id)}>Supprimer</button>
                      </td>
                    </tr>
                  ))}
                  {tiers.length === 0 && (
                    <tr><td colSpan={4} className="mg-empty">Aucun palier pour cette portee.</td></tr>
                  )}
                </tbody>
              </table>

              <form className="mg-new-form" onSubmit={handleCreateTier}>
                <div className="mg-new-title">+ Nouveau palier</div>
                <div className="mg-new-row">
                  <input className="mg-cell-input" placeholder="Min" value={newTier.min_amount} onChange={(e) => setNewTier((p) => ({ ...p, min_amount: e.target.value }))} required />
                  <input className="mg-cell-input" placeholder="Max" value={newTier.max_amount} onChange={(e) => setNewTier((p) => ({ ...p, max_amount: e.target.value }))} required />
                  <input className="mg-cell-input" placeholder="Gratis" value={newTier.free_count} onChange={(e) => setNewTier((p) => ({ ...p, free_count: e.target.value }))} required />
                  <button type="submit" className="mg-add-btn" disabled={creating}>{creating ? 'Ajout...' : 'Ajouter'}</button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MarriageGratisPage;
